import { useRef } from 'react';
import { Box } from '@mui/material';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { playRoadDraw, setAuthHeroEndState } from './authHeroAnimations';
import { useTvTextEntry } from 'hooks/screen/useTvTextEntry';

gsap.registerPlugin(useGSAP);

const LINE_Y = 36;

type AuthBrandHeroProps = {
  logoSrc: string;
  truckSrc: string;
};

export function AuthBrandHero({ logoSrc, truckSrc }: AuthBrandHeroProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const truckRef = useRef<HTMLImageElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const roadLineRef = useRef<SVGPathElement>(null);

  const { isTv } = useTvTextEntry();
  const reduceMotion =
    isTv ||
    (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  useGSAP(
    () => {
      const logo = logoRef.current;
      const truck = truckRef.current;
      const stage = stageRef.current;
      const roadLine = roadLineRef.current;
      if (!logo || !truck || !stage || !roadLine) {
        return;
      }

      const nodes = { logo, truck, stage, roadLine };

      if (reduceMotion) {
        setAuthHeroEndState(nodes);
        return;
      }

      playRoadDraw(nodes);
    },
    {
      scope: rootRef,
      dependencies: [reduceMotion],
      revertOnUpdate: true,
    },
  );

  return (
    <Box ref={rootRef} sx={{ width: '100%', mb: { xs: 2, sm: 2.5 }, textAlign: 'center' }}>
      <Box
        component="h1"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          m: 0,
          mb: 1.5,
        }}
      >
        <Box
          component="img"
          ref={logoRef}
          src={logoSrc}
          alt="Optic Truck Works"
          sx={{
            width: '100%',
            maxWidth: { xs: 260, sm: 300 },
            height: 'auto',
            willChange: 'transform',
          }}
        />
      </Box>

      <Box
        ref={stageRef}
        sx={{
          position: 'relative',
          height: { xs: 72, sm: 80 },
          overflow: 'hidden',
        }}
      >
        <Box
          component="svg"
          viewBox="0 0 320 56"
          sx={{ width: '100%', height: '100%', display: 'block' }}
        >
          <path
            ref={roadLineRef}
            d={`M8 ${LINE_Y} H312`}
            fill="none"
            stroke="#455573"
            strokeWidth="3"
            strokeLinecap="round"
          />
        </Box>
        <Box
          component="img"
          ref={truckRef}
          src={truckSrc}
          alt=""
          sx={{
            position: 'absolute',
            bottom: { xs: 14, sm: 16 },
            left: 0,
            height: { xs: 52, sm: 60 },
            width: 'auto',
            willChange: 'transform',
            filter: 'drop-shadow(0 3px 4px rgba(26, 36, 54, 0.28))',
          }}
        />
      </Box>
    </Box>
  );
}
